import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { createItgcDomain, type CreateItgcDomainResult } from '../../../request/controls/controlItgcOptions.ts';
import { resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';

type CreateItgcDomainModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (domain: CreateItgcDomainResult) => void;
};

export function CreateItgcDomainModal({ isOpen, onClose, onCreated }: CreateItgcDomainModalProps) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setCode('');
    setName('');
    setSaving(false);
    setError(null);
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleCreate = async () => {
    const trimmedCode = code.trim();
    const trimmedName = name.trim();
    if (!trimmedCode) {
      setError('Code is required');
      return;
    }
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createItgcDomain({ code: trimmedCode, name: trimmedName });
      onCreated(created);
      onClose();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(
        resolve403ErrorMessage(
          err,
          axiosErr?.response?.data?.error || 'Something went wrong. Please try again.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        disabled={saving}
        aria-label="Close add domain popup"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add domain"
        className="relative z-10 w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Add domain</h3>
            <p className="mt-1 text-sm text-gray-600">Create a custom ITGC domain for this organization.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="itgc-domain-code" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Code <span className="text-red-500">*</span>
            </label>
            <input
              id="itgc-domain-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={saving}
              maxLength={32}
              placeholder="e.g. AM"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-gray-50"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleCreate();
                }
              }}
            />
          </div>
          <div>
            <label htmlFor="itgc-domain-name" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="itgc-domain-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              maxLength={255}
              placeholder="e.g. Asset Management"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-gray-50"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleCreate();
                }
              }}
            />
          </div>

          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md border border-blue-600 bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
